import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { useAuth } from '../contexts/AuthContext';
import { orgCatalogService } from '../services/orgCatalogService';
import { ArrowLeft, ChefHat, Loader2, Building2, Crown, Link2, X, Check, AlertTriangle, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';

const cleanErr = (err) => (err?.message || 'Error').replace('Firebase: ', '').replace(/\s*\(.*\)$/, '');

const Toggle = ({ on, onClick, disabled }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`inline-flex items-center w-12 h-7 rounded-full transition-colors flex-shrink-0 disabled:opacity-50 ${on ? 'bg-kumia-green' : 'bg-surface-5'}`}
  >
    <span className={`inline-block w-5 h-5 rounded-full bg-white shadow transition-transform ${on ? 'translate-x-6' : 'translate-x-1'}`} />
  </button>
);

/**
 * Modal de mapeo de insumos: enlaza cada insumo de la PRINCIPAL con el insumo local
 * equivalente de la sucursal, para que las recetas heredadas calculen costo local.
 */
const MappingModal = ({ sede, onClose, onChanged }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingPid, setSavingPid] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await orgCatalogService.getInventoryLinking({ restaurantId: sede.id });
      setData(res);
    } catch (err) {
      toast.error(cleanErr(err));
    } finally {
      setLoading(false);
    }
  }, [sede.id]);

  useEffect(() => { load(); }, [load]);

  const sortedPrimary = useMemo(() => {
    const items = data?.primaryItems || [];
    return [...items].sort((a, b) => (b.referenced ? 1 : 0) - (a.referenced ? 1 : 0) || a.name.localeCompare(b.name));
  }, [data]);

  const localByPrimary = useMemo(() => {
    const map = {};
    (data?.localItems || []).forEach((l) => { if (l.primaryItemId) map[l.primaryItemId] = l.id; });
    return map;
  }, [data]);

  const handleMap = async (primaryItem, newLocalId) => {
    setSavingPid(primaryItem.id);
    try {
      const current = (data?.localItems || []).find((l) => l.primaryItemId === primaryItem.id);
      if (!newLocalId) {
        if (current) await orgCatalogService.setInventoryItemLink({ restaurantId: sede.id, itemId: current.id, primaryItemId: null });
      } else {
        if (current && current.id !== newLocalId) {
          await orgCatalogService.setInventoryItemLink({ restaurantId: sede.id, itemId: current.id, primaryItemId: null });
        }
        await orgCatalogService.setInventoryItemLink({ restaurantId: sede.id, itemId: newLocalId, primaryItemId: primaryItem.id });
      }
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(cleanErr(err));
    } finally {
      setSavingPid(null);
    }
  };

  const summary = data?.summary;

  return createPortal(
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[9999] p-4" onClick={onClose}>
      <div className="bg-surface-1 border border-line rounded-xl max-w-lg w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-line-subtle flex-shrink-0">
          <div className="min-w-0">
            <h3 className="text-content-primary font-semibold text-sm flex items-center gap-2"><Link2 size={15} className="text-kumia-green" /> Mapear insumos</h3>
            <p className="text-content-subtle text-xs mt-0.5 truncate">{sede.locationName || sede.name}</p>
          </div>
          <button onClick={onClose} className="text-content-subtle hover:text-content-primary"><X size={18} /></button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 size={24} className="animate-spin text-kumia-green" /></div>
        ) : (
          <div className="overflow-y-auto p-5 space-y-3">
            {summary && (
              <div className={`text-sm rounded-lg p-3 border ${summary.mappedNeededCount >= summary.neededCount ? 'bg-kumia-green/10 border-kumia-green/30 text-content-secondary' : 'bg-yellow-500/10 border-yellow-500/30 text-yellow-700 dark:text-yellow-300'}`}>
                Insumos usados en recetas mapeados: <strong>{summary.mappedNeededCount}/{summary.neededCount}</strong>
                {summary.mappedNeededCount < summary.neededCount && ' — las recetas con insumos sin mapear quedan inactivas.'}
              </div>
            )}
            <p className="text-content-subtle text-xs">Elegí, para cada insumo de la principal, el insumo equivalente de esta sucursal (misma unidad base).</p>

            {sortedPrimary.length === 0 ? (
              <div className="text-center py-8 text-content-muted text-sm">La principal no tiene insumos cargados.</div>
            ) : sortedPrimary.map((p) => {
              const localOptions = (data?.localItems || []).filter((l) => l.baseUnit === p.baseUnit);
              const value = localByPrimary[p.id] || '';
              return (
                <div key={p.id} className="flex items-center gap-2 p-2.5 rounded-lg bg-surface-3/40 border border-line-strong/40">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm text-content-primary truncate">{p.name}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-surface-5 text-content-subtle flex-shrink-0">{p.baseUnit}</span>
                      {p.referenced && <span className="text-[10px] px-1.5 py-0.5 rounded bg-kumia-green/15 text-kumia-green flex-shrink-0">en recetas</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {value ? <Check size={14} className="text-kumia-green" /> : <AlertTriangle size={14} className="text-yellow-600" />}
                    <select
                      value={value}
                      disabled={savingPid === p.id}
                      onChange={(e) => handleMap(p, e.target.value)}
                      className="w-40 px-2 py-1.5 bg-surface-3 border border-line-active rounded-lg text-content-primary text-xs focus:outline-none focus:ring-2 focus:ring-kumia-green/50 disabled:opacity-50"
                    >
                      <option value="">— Sin mapear —</option>
                      {localOptions.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                    </select>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

/**
 * Recetas compartidas (Fase 2.3, modelo "principal = fuente"):
 * Las recetas las define la SEDE PRINCIPAL en su módulo de inventario. Esta pantalla decide
 * qué SUCURSALES las heredan, y permite MAPEAR los insumos de cada sucursal con los de la
 * principal (para calcular el costo local). El costo se calcula con los precios de cada sede.
 */
const OrgRecipes = () => {
  const navigate = useNavigate();
  const { isGroupOwner, loading: authLoading } = useAuth();

  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [syncingId, setSyncingId] = useState(null);
  const [sedes, setSedes] = useState([]);
  const [mappingSede, setMappingSede] = useState(null);

  useEffect(() => {
    if (!authLoading && !isGroupOwner) navigate('/chain', { replace: true });
  }, [authLoading, isGroupOwner, navigate]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const locs = await orgCatalogService.getMyLocations();
      setSedes(locs?.locations || []);
    } catch (err) {
      console.error('[OrgRecipes] load error:', err);
      toast.error('No se pudieron cargar las sedes.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (isGroupOwner) load(); }, [isGroupOwner, load]);

  const primary = useMemo(() => sedes.find((s) => s.isPrimaryLocation), [sedes]);
  const branches = useMemo(() => sedes.filter((s) => !s.isPrimaryLocation), [sedes]);

  const handleSync = async (sede) => {
    setSyncingId(sede.id);
    try {
      const res = await orgCatalogService.provisionInventoryFromPrimary({ restaurantId: sede.id });
      toast.success(res?.message || 'Inventario sincronizado desde la principal.');
    } catch (err) {
      toast.error(cleanErr(err));
    } finally {
      setSyncingId(null);
    }
  };

  const toggleConnected = async (sede) => {
    const connectedNow = sede.catalogOverrides?.recipes !== true;
    setBusyId(sede.id);
    try {
      await orgCatalogService.setLocationOverride({ restaurantId: sede.id, domain: 'recipes', override: connectedNow });
      setSedes((prev) => prev.map((s) => s.id === sede.id
        ? { ...s, catalogOverrides: { ...(s.catalogOverrides || {}), recipes: connectedNow } }
        : s));
      toast.success(connectedNow
        ? 'Sucursal desconectada: gestionará sus propias recetas (conservó las heredadas).'
        : 'Sucursal conectada: heredará las recetas de la principal. Mapeá sus insumos.');
    } catch (err) {
      toast.error(cleanErr(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-surface-base via-surface-app to-surface-3">
      <header className="border-b border-line-subtle bg-surface-chrome/80 dark:bg-black/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center gap-3">
          <button onClick={() => navigate('/chain')} className="text-content-subtle hover:text-content-primary p-1.5 rounded-lg hover:bg-surface-3 flex-shrink-0"><ArrowLeft size={20} /></button>
          <ChefHat size={18} className="text-kumia-green flex-shrink-0" />
          <h1 className="text-lg font-bold text-content-primary truncate">Recetas · Sucursales</h1>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 size={28} className="animate-spin text-kumia-green" /></div>
        ) : (
          <div className="space-y-6">
            <div className="bg-kumia-green/10 border border-kumia-green/30 rounded-xl p-4 text-sm text-content-secondary">
              Las <strong className="text-content-primary">recetas</strong> las define la <strong className="text-content-primary">sede principal</strong> en su inventario.
              Acá elegís qué sucursales las heredan; el <strong className="text-content-primary">costo se calcula con los precios de cada sucursal</strong>.
              <span className="block mt-1 text-content-subtle">Para una sucursal nueva, tocá <strong>“Sincronizar desde la principal”</strong>: crea y mapea sus insumos automáticamente (stock y costo arrancan en 0).</span>
            </div>

            {primary && (
              <div className="flex items-center gap-2 p-4 rounded-xl bg-surface-3/40 border border-line-strong/50">
                <Crown size={16} className="text-kumia-green flex-shrink-0" />
                <span className="text-sm text-content-primary font-medium truncate">{primary.locationName || primary.name}</span>
                <span className="ml-auto text-xs px-2 py-1 rounded-full bg-kumia-green/15 border border-kumia-green/40 text-kumia-green flex-shrink-0">Fuente de recetas</span>
              </div>
            )}

            <div>
              <h2 className="text-content-primary font-semibold mb-1">Sucursales</h2>
              <p className="text-content-subtle text-sm mb-4">
                <strong>Conectada</strong>: hereda las recetas (mapeá sus insumos). <strong>Independiente</strong>: gestiona las suyas.
              </p>

              {branches.length === 0 ? (
                <div className="text-center py-10 bg-surface-3/30 rounded-xl border border-line-strong/40">
                  <Building2 size={32} className="mx-auto text-content-subtle mb-2" />
                  <p className="text-content-muted text-sm">No hay sucursales todavía.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {branches.map((sede) => {
                    const connected = sede.catalogOverrides?.recipes !== true;
                    const busy = busyId === sede.id;
                    return (
                      <div key={sede.id} className="flex items-center justify-between gap-3 p-3 rounded-lg bg-surface-3/40 border border-line-strong/40">
                        <div className="flex items-center gap-2 min-w-0">
                          <Building2 size={15} className="text-content-subtle flex-shrink-0" />
                          <div className="min-w-0">
                            <span className="text-sm text-content-primary truncate block">{sede.locationName || sede.name}</span>
                            <span className={`text-xs ${connected ? 'text-kumia-green' : 'text-content-subtle'}`}>
                              {connected ? 'Hereda las recetas de la principal' : 'Independiente'}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {connected && (
                            <>
                              <button
                                onClick={() => handleSync(sede)}
                                disabled={busy || syncingId === sede.id}
                                title="Crea y mapea los insumos de la principal en esta sucursal (stock y costo arrancan en 0)."
                                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-kumia-green/15 border border-kumia-green/40 text-kumia-green hover:bg-kumia-green/25 disabled:opacity-40"
                              >
                                {syncingId === sede.id ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Sincronizar
                              </button>
                              <button
                                onClick={() => setMappingSede(sede)}
                                disabled={busy}
                                title="Revisar / ajustar el mapeo de insumos manualmente"
                                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border border-line-strong bg-surface-3 hover:bg-surface-5 text-content-secondary disabled:opacity-40"
                              >
                                <Link2 size={12} /> Revisar
                              </button>
                            </>
                          )}
                          <Toggle on={connected} onClick={() => toggleConnected(sede)} disabled={busy} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {mappingSede && (
        <MappingModal sede={mappingSede} onClose={() => setMappingSede(null)} onChanged={() => {}} />
      )}
    </div>
  );
};

export default OrgRecipes;
